import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import SearchableCombobox from "@/components/SearchableCombobox";
import { DOCUMENT_CATEGORIES } from "@/lib/constants";
import { PencilSimple, Lock } from "@phosphor-icons/react";

const F = ({ label, children }) => <div><Label className="text-xs">{label}</Label>{children}</div>;
const cname = (c) => c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "—";
const fromDoc = (d) => ({
  title: d.title || "", document_date: (d.document_date || "").slice(0, 10), category: d.category || "Sonstiges",
  direction: d.direction || "internal", sender_name: d.sender_name || "", notes: d.notes || "",
  linked_property_id: d.linked_property_id || "", linked_tenancy_id: d.linked_tenancy_id || "", linked_contact_id: d.linked_contact_id || "",
});

function useLinkOptions(open) {
  const [o, setO] = useState({ properties: [], tenancies: [], contacts: [] });
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/properties"), api.get("/tenancies", { params: { status: "all" } }), api.get("/contacts", { params: { limit: 1000 } })])
      .then(([p, t, c]) => setO({
        properties: (p.data || []).map((x) => ({ id: x.id, label: x.name || x.property_number, sublabel: x.property_number })),
        tenancies: (t.data || []).map((x) => ({ id: x.id, label: `${x.contract_number || ""} ${(x.tenant_names || []).join(", ")}`.trim(), sublabel: [x.property_name, x.unit_label].filter(Boolean).join(" · ") })),
        contacts: (c.data || []).map((x) => ({ id: x.id, label: cname(x) })),
      })).catch(() => {});
  }, [open]);
  return o;
}

export function DocumentMetaDialog({ doc, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(() => fromDoc(doc));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(fromDoc(doc)); }, [open, doc]);
  const opts = useLinkOptions(open);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const locked = !!doc.archive_locked;
  const save = async () => {
    setBusy(true);
    const body = { ...f };
    if (!body.document_date) delete body.document_date;
    if (locked) { delete body.title; delete body.direction; }
    try { const { data } = await api.patch(`/documents/${doc.id}`, body); toast.success("Dokument aktualisiert"); onSaved?.(data); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const box = (k, label, options) => <F label={label}><SearchableCombobox value={f[k]} onChange={(v) => set(k, v || "")} options={options.length || !f[k] ? options : [{ id: f[k], label: "Lade…" }]} placeholder="— keine Zuordnung —" testId={`docmeta-${k}`} /></F>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="docmeta-dialog">
        <DialogHeader><DialogTitle>Dokument bearbeiten</DialogTitle><DialogDescription>{doc.original_filename || doc.title}</DialogDescription></DialogHeader>
        {locked && <div className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"><Lock size={13} /> Versendetes Schreiben – Titel und Richtung sind archiviert und nicht änderbar.</div>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F label="Titel"><Input value={f.title} disabled={locked} onChange={(e) => set("title", e.target.value)} data-testid="docmeta-title" /></F></div>
          <F label="Dokumentdatum"><DatePicker value={f.document_date} onChange={(v) => set("document_date", v)} placeholder={doc.created_at ? `hochgeladen ${doc.created_at.slice(8, 10)}.${doc.created_at.slice(5, 7)}.${doc.created_at.slice(0, 4)}` : "Datum wählen"} data-testid="docmeta-date" /></F>
          <F label="Kategorie">
            <Select value={f.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="docmeta-category"><SelectValue /></SelectTrigger>
              <SelectContent>{[...new Set([...DOCUMENT_CATEGORIES, f.category])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Richtung">
            <Select value={f.direction} onValueChange={(v) => set("direction", v)} disabled={locked}>
              <SelectTrigger data-testid="docmeta-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="in">Eingehend</SelectItem><SelectItem value="out">Ausgehend</SelectItem><SelectItem value="internal">Intern</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Aussteller / Absender (optional)"><Input value={f.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="z. B. Stadtverwaltung" data-testid="docmeta-sender" /></F>
          {box("linked_property_id", "Objekt", opts.properties)}
          {box("linked_tenancy_id", "Mietverhältnis", opts.tenancies)}
          <div className="col-span-2">{box("linked_contact_id", "Kontakt", opts.contacts)}</div>
          <div className="col-span-2"><F label="Beschreibung / Notiz"><Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="docmeta-notes" /></F></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="docmeta-save">{busy ? "Speichere…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DocMetaButton({ doc, onSaved, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" className={className} onClick={() => setOpen(true)} title="Metadaten bearbeiten" data-testid={`docmeta-btn-${doc.id}`}><PencilSimple size={14} /></Button>
      {open && <DocumentMetaDialog doc={doc} open={open} onOpenChange={setOpen} onSaved={onSaved} />}
    </>
  );
}

export const docDate = (d) => d.document_date || d.created_at;
